import type { PaginationMeta } from '@/types/shared'

// Mirrors the AdminInvoiceDocument resource in
// documentation/feature-requests/platform-fee-invoicing-m2.md section 5.9.3.

export const InvoiceDocumentType = {
  FeeInvoice: 'fee_invoice',
  CreditPurchaseInvoice: 'credit_purchase_invoice',
  CreditNote: 'credit_note',
} as const

export type InvoiceDocumentType = typeof InvoiceDocumentType[keyof typeof InvoiceDocumentType]

// Rendering status of the document itself (not the delivery status).
export const InvoiceRenderingStatus = {
  Pending: 'pending',
  Generated: 'generated',
  Failed: 'failed',
  // An older version replaced by a newer generation of the same document; accepted by the status filter.
  Superseded: 'superseded',
} as const

export type InvoiceRenderingStatus = typeof InvoiceRenderingStatus[keyof typeof InvoiceRenderingStatus]

export const InvoiceDeliveryChannel = {
  Email: 'email',
  Peppol: 'peppol',
} as const

export type InvoiceDeliveryChannel = typeof InvoiceDeliveryChannel[keyof typeof InvoiceDeliveryChannel]

export const InvoiceDeliveryStatus = {
  Pending: 'pending',
  Sent: 'sent',
  Failed: 'failed',
} as const

export type InvoiceDeliveryStatus = typeof InvoiceDeliveryStatus[keyof typeof InvoiceDeliveryStatus]

export const InvoiceChannelSource = {
  Derived: 'derived',
  OrganisationSetting: 'organisation_setting',
  AdminOverride: 'admin_override',
  CreditedDocument: 'credited_document',
} as const

export type InvoiceChannelSource = typeof InvoiceChannelSource[keyof typeof InvoiceChannelSource]

export const DocumentFileVariant = {
  Pdf: 'pdf',
  Copy: 'copy',
  Ubl: 'ubl',
  DeliveredUbl: 'delivered_ubl',
} as const

export type DocumentFileVariant = typeof DocumentFileVariant[keyof typeof DocumentFileVariant]

export const VatRegime = {
  SmallEnterpriseExempt: 'small_enterprise_exempt',
  Standard: 'standard',
} as const

export type VatRegime = typeof VatRegime[keyof typeof VatRegime]

export const TurnoverGateState = {
  Open: 'open',
  ThresholdExceeded: 'threshold_exceeded',
  UnsupportedRegime: 'unsupported_regime',
  NoThreshold: 'no_threshold',
} as const

export type TurnoverGateState = typeof TurnoverGateState[keyof typeof TurnoverGateState]

export interface AdminInvoiceDocumentOrganisation {
  id: number
  name: string
  slug: string
  country_code: string | null
  vat_number: string | null
}

export interface AdminInvoiceDocumentDelivery {
  channel: InvoiceDeliveryChannel
  channel_source: InvoiceChannelSource
  status: InvoiceDeliveryStatus
  provider: string | null
  provider_reference: string | null
  recipient_identifier: string | null
  recipient_email: string | null
  lookup_note: string | null
  email_attempts: number
  sent_at: string | null
  delivered_at: string | null
  notified_at: string | null
  failure_reason: string | null
  provider_synced_at: string | null
  recorded_by: { id: number; name: string } | null
}

export interface AdminInvoiceDocument {
  id: number
  type: InvoiceDocumentType
  document_number: string
  status: InvoiceRenderingStatus
  failure_reason: string | null
  issued_at: string
  period_start: string
  period_end: string
  period_sequence: number | null
  organisation: AdminInvoiceDocumentOrganisation
  // Both are null when the document summary carries no totals.
  totals: { currency: string | null; gross: string | null }
  files: Record<DocumentFileVariant, boolean>
  checksums: {
    pdf: string | null
    ubl: string | null
    delivered_ubl: string | null
  }
  // Present whenever the delivery relation is loaded (always, on the list endpoint);
  // null when the document has no delivery row.
  delivery?: AdminInvoiceDocumentDelivery | null
  // M3 additive fields (spec platform-fee-invoicing-m3.md section 5.13).
  source_type: string | null
  // Credit notes only: the number of the invoice they credit.
  // Absent on invoices (the backend only includes it for credit notes).
  credited_document_number?: string | null
  // Decimal string: the sum of the credit items that credit this document's items.
  credited_total: string | null
  // Set once the invoice was credited in full and reissued.
  // Absent when the relation was not loaded.
  correction?: AdminInvoiceDocumentCorrection | null
  // Reissued invoices only: the number of the invoice they replace.
  replaces_document_number: string | null
  // Id of a fee return on this invoice that is not in a terminal state yet.
  // Absent when the relation was not loaded.
  open_fee_return_id?: number | null
}

export interface AdminInvoiceDocumentCorrection {
  id: number
  credit_note_number: string
  reissued_number: string
}

export interface InvoiceDocumentCounts {
  pending_peppol: number
  failed: number
  unsynced: number
}

export interface AdminInvoiceDocumentListResponse {
  data: AdminInvoiceDocument[]
  links: Record<string, string | null>
  meta: PaginationMeta & { counts: InvoiceDocumentCounts }
}

// Tri-state: null = all, true = synced only, false = not synced only.
export interface InvoiceDocumentFilters {
  month: string | null
  type: InvoiceDocumentType | null
  channel: InvoiceDeliveryChannel | null
  delivery_status: InvoiceDeliveryStatus | null
  synced: boolean | null
  status: InvoiceRenderingStatus | null
  search: string
  per_page: number
  page: number
}

export interface AdminDocumentDownload {
  url: string
  expires_at: string
  filename: string
  variant: DocumentFileVariant
}

export interface RecordPeppolDeliveryPayload {
  ubl_file: File
  provider_reference: string | null
  sent_on: string
  confirm_amount_mismatch: boolean
}

export interface MarkSyncedResult {
  updated_ids: number[]
  skipped: { id: number; reason: string }[]
}

export interface InvoiceExportRequest {
  month: string
  recipient_email: string
  document_count: number
}

export interface InvoicingTurnover {
  year: number
  threshold: string | null
  invoiced: string
  uninvoiced: string
  credited: string
  total: string
  percentage: number | null
  currency: string
  // Null when no regime row covers the current moment.
  regime_in_force: VatRegime | null
  gate: TurnoverGateState
  regime_recorded_this_year: boolean
  // M3: one entry per alert level of the year, reached or not.
  levels: ThresholdLevel[]
  next_year_threshold_configured: boolean
}

export interface ThresholdLevel {
  level: number
  reached: boolean
  raised_at: string | null
}

export interface VatRegimePeriod {
  id: number
  regime: VatRegime
  // ISO timestamp.
  effective_from: string
  source: string
  note: string | null
  recorded_by: { id: number; name: string } | null
  created_at: string
}

export interface RecordVatRegimePayload {
  regime: VatRegime
  // Europe/Brussels, formatted Y-m-d H:i.
  effective_from: string
  note: string
  acknowledge_affected_documents?: boolean
}

export interface RecordedVatRegime {
  period: VatRegimePeriod
  affectedDocumentNumbers: string[]
}

export interface OrganisationInvoicingSummary {
  delivery_channel_override: InvoiceDeliveryChannel | null
  derived_channel: InvoiceDeliveryChannel
  peppol_identifier: string | null
}

export interface OrganisationInvoiceChannel {
  organisation_id: number
  delivery_channel_override: InvoiceDeliveryChannel | null
  derived_channel: InvoiceDeliveryChannel
}

export interface OrganisationInvoiceChannelPayload {
  channel: InvoiceDeliveryChannel | null
  reason: string
}

// ---------------------------------------------------------------------------
// M3: fee returns, credit and reissue, reconciliation
// ---------------------------------------------------------------------------

export const PlatformFeeReturnStatus = {
  Pending: 'pending',
  Refunding: 'refunding',
  Refunded: 'refunded',
  Completed: 'completed',
  Failed: 'failed',
  NeedsAttention: 'needs_attention',
} as const

export type PlatformFeeReturnStatus = typeof PlatformFeeReturnStatus[keyof typeof PlatformFeeReturnStatus]

export const PlatformFeeReturnLineStatus = {
  Pending: 'pending',
  Refunded: 'refunded',
  Failed: 'failed',
} as const

export type PlatformFeeReturnLineStatus = typeof PlatformFeeReturnLineStatus[keyof typeof PlatformFeeReturnLineStatus]

export const PlatformFeeReturnMode = {
  Refund: 'refund',
  Adopt: 'adopt',
} as const

export type PlatformFeeReturnMode = typeof PlatformFeeReturnMode[keyof typeof PlatformFeeReturnMode]

export const FeeReturnReason = {
  TechnicalError: 'technical_error',
  GoodwillDispute: 'goodwill_dispute',
  BillingError: 'billing_error',
} as const

export type FeeReturnReason = typeof FeeReturnReason[keyof typeof FeeReturnReason]

export const InvoiceCorrectionReason = {
  BillingDetails: 'billing_details',
  Other: 'other',
} as const

export type InvoiceCorrectionReason = typeof InvoiceCorrectionReason[keyof typeof InvoiceCorrectionReason]

export const FeeRefundDiscrepancyKind = {
  UnrecordedStripeRefund: 'unrecorded_stripe_refund',
  MissingStripeRefund: 'missing_stripe_refund',
  CreditNoteMissing: 'credit_note_missing',
} as const

export type FeeRefundDiscrepancyKind = typeof FeeRefundDiscrepancyKind[keyof typeof FeeRefundDiscrepancyKind]

export const FeeRefundDiscrepancyStatus = {
  Open: 'open',
  Resolved: 'resolved',
} as const

export type FeeRefundDiscrepancyStatus = typeof FeeRefundDiscrepancyStatus[keyof typeof FeeRefundDiscrepancyStatus]

export const FeeRefundDiscrepancyResolution = {
  CreditNoteIssued: 'credit_note_issued',
  Dismissed: 'dismissed',
  AutoCleared: 'auto_cleared',
} as const

export type FeeRefundDiscrepancyResolution = typeof FeeRefundDiscrepancyResolution[keyof typeof FeeRefundDiscrepancyResolution]

// A return is finished (nothing left to poll) in these states.
export const TERMINAL_FEE_RETURN_STATUSES: readonly PlatformFeeReturnStatus[] = [
  PlatformFeeReturnStatus.Completed,
  PlatformFeeReturnStatus.Failed,
  PlatformFeeReturnStatus.NeedsAttention,
]

export interface EligibilityRefusal {
  code: string
  message: string
}

// Returnable-items endpoint: at most one refusal, nested under `refusal` (null when allowed).
export interface ReturnEligibility {
  allowed: boolean
  refusal: EligibilityRefusal | null
}

export interface ReturnableFeeItem {
  item_id: number
  registration_id: number
  reference: string
  paid_on: string
  event: string
  race: string | null
  invoiced: string
  credited: string
  in_flight: string
  returnable: string
}

export interface ReturnableFeeItems {
  document: {
    id: number
    document_number: string
    issue_date: string
    period_start: string
    period_end: string
  }
  organisation: { id: number; name: string; slug: string }
  currency: string
  eligibility: ReturnEligibility
  items: ReturnableFeeItem[]
  totals: { invoiced: string; credited: string; returnable: string }
}

export interface PlatformFeeReturnLine {
  id: number
  item_id: number
  registration_id: number
  amount: string
  status: PlatformFeeReturnLineStatus
  failure_reason: string | null
  refunded_at: string | null
  stripe_application_fee_id: string | null
  stripe_fee_refund_id: string | null
}

export interface PlatformFeeReturn {
  id: number
  status: PlatformFeeReturnStatus
  mode: PlatformFeeReturnMode
  reason: FeeReturnReason
  note: string
  currency: string
  requested_total: string
  refunded_total: string
  failure_reason: string | null
  organisation: { id: number; name: string; slug: string }
  credited_document: { id: number; document_number: string }
  credit_note: { id: number; document_number: string; status: InvoiceRenderingStatus } | null
  requested_by: { id: number; name: string } | null
  lines: PlatformFeeReturnLine[]
  created_at: string
  refunded_at: string | null
  completed_at: string | null
}

export interface FeeReturnLinePayload {
  item_id: number
  // Euro decimal string, for example "0.75".
  amount: string
}

export interface CreateFeeReturnPayload {
  request_id: string
  reason: FeeReturnReason
  note: string
  lines: FeeReturnLinePayload[]
}

export interface AdoptDiscrepancyPayload {
  request_id: string
  reason: FeeReturnReason
  note: string
}

export interface PlatformFeeReturnFilters {
  status: PlatformFeeReturnStatus | null
  per_page: number
  page: number
}

export interface CustomerBlock {
  billing_name: string | null
  address_line_1: string | null
  address_line_2: string | null
  postal_code: string | null
  city: string | null
  country_code: string | null
  vat_number: string | null
}

export type CustomerField = keyof CustomerBlock

export interface CorrectionRefusal {
  code: string
  message: string
}

// Mirrors InvoiceCorrectionPreviewResource.
export interface InvoiceCorrectionPreview {
  document: {
    id: number
    document_number: string
    type: InvoiceDocumentType
    issue_date: string
    period_start: string
    period_end: string
    // Gross total of the invoice; null when its summary carries no totals.
    total: string | null
  }
  current_customer: CustomerBlock
  // Null when the current billing data cannot build a customer block (reported as a refusal).
  new_customer: CustomerBlock | null
  differences: CustomerField[]
  amounts: {
    currency: string
    credit_total: string
    reissue_total: string
  }
  channels: {
    credit_note_channel: InvoiceDeliveryChannel
    reissue_channel: InvoiceDeliveryChannel | null
  }
  eligibility: { allowed: boolean; refusals: CorrectionRefusal[] }
}

export interface CreateInvoiceCorrectionPayload {
  request_id: string
  reason: InvoiceCorrectionReason
  note: string
}

export interface InvoiceCorrection {
  id: number
  reason: InvoiceCorrectionReason
  note: string
  created_at: string
  credited_document: { id: number; document_number: string }
  credit_note: { id: number; document_number: string; status: InvoiceRenderingStatus }
  reissued_document: { id: number; document_number: string; status: InvoiceRenderingStatus }
  requested_by: { id: number; name: string } | null
}

export interface FeeRefundDiscrepancy {
  id: number
  kind: FeeRefundDiscrepancyKind
  status: FeeRefundDiscrepancyStatus
  resolution: FeeRefundDiscrepancyResolution | null
  resolution_note: string | null
  stripe_application_fee_id: string | null
  stripe_fee_refund_id: string | null
  stripe_amount: string | null
  local_amount: string | null
  currency: string | null
  registration_id: number | null
  organisation: { id: number; name: string; slug: string } | null
  document: { id: number; document_number: string } | null
  item_id: number | null
  platform_fee_return_id: number | null
  adoptable: boolean
  first_detected_at: string
  last_detected_at: string
  resolved_at: string | null
  resolved_by: { id: number; name: string } | null
}

export interface FeeRefundDiscrepancyFilters {
  status: FeeRefundDiscrepancyStatus
  per_page: number
  page: number
}
